import type {
  AnalyticsOptions,
  AnalyticsProperties,
  RemoteAnalytics,
  ShellAnalytics,
} from '@wandb/forge-shell-contract';

import {ARIA_PRODUCT_ID} from '../header/forgeProducts';
import type {RemoteName} from '../shellConfig';
import {hostSessionId, resetHostSessionForTests} from './hostSession';

/**
 * Everything in the shell that touches the Segment global, `window.analytics`.
 *
 * Loading the vendors is `consent/analytics.ts`, which the shell reaches by
 * import rather than through a global.
 *
 * There are two ways into Segment and one instance underneath:
 *
 *   window.analytics  ->  stamping proxy  ->  instance   (wandb's ~250 legacy
 *                                                         call sites)
 *   createAnalytics() ------------------->    instance   (the contract, and the
 *                                                         shell's own events)
 *
 * Each path stamps `app_name` on its own, so a legacy call and a contract call
 * produce the same payload.
 *
 * A handle reaches Segment through `instance()`, never by reading
 * `window.analytics`. Reading the global would put the proxy in front of it,
 * and the proxy stamps whichever remote is currently *mounted* rather than the
 * name the handle was built with — so `analyticsForShell.track(...)`, fired
 * while the wandb remote is on screen, would arrive as `app_name: 'wandb'`.
 */

/** The part of the loaded Segment library (analytics.js) the shell calls. */
export type SegmentApi = {
  track(event: string, properties?: object, options?: object): void;
  page(properties?: object, options?: object): void;
  identify(userId: string, traits?: object): void;
  group(groupId: string, traits?: object): void;
  /** Queued by the snippet; analytics.js fires it once destinations are up. */
  ready?(callback: () => void): void;
  /** Set by analytics.js once the real library has replaced the snippet. */
  initialized?: boolean;
};

declare global {
  interface Window {
    analytics?: SegmentApi;
  }
}

/**
 * `app_name` for calls that are not a remote's: the shell's own chrome, the
 * home page, the identity pages.
 */
export const SHELL_APP_NAME = 'shell';

// --- The instance ----------------------------------------------------------

let segment: SegmentApi | undefined;
let accessorInstalled = false;

/**
 * Reading `window.analytics` can throw outright — `window?.analytics` does not
 * help, because it is the property access that throws, not the optional chain.
 */
function readGlobal(): SegmentApi | undefined {
  try {
    return window.analytics;
  } catch {
    return undefined;
  }
}

/**
 * The real instance, never the proxy. Falls back to the raw global before the
 * accessor is installed, and in tests that never install it — the flag matters,
 * because reading the global after install would come back through the proxy's
 * getter and recurse.
 */
function instance(): SegmentApi | undefined {
  return accessorInstalled ? segment : readGlobal();
}

// --- Calling Segment -------------------------------------------------------

let warnedMissing = false;

/**
 * Calls Segment if it is present at all, initialized or not.
 *
 * Deliberately *unlike* `frontends/app/src/services/analytics/Analytics.ts`,
 * which drops calls while `initialized` is false. The snippet's stubs queue
 * calls and analytics.js replays them on load, so passing through loses nothing
 * when Segment is merely still loading. That distinction barely mattered in the
 * standalone app, where the injector and the app bundle load together; under
 * the shell the gap is much wider — shell boot, viewer query,
 * `initializeTrackingScripts`, OneTrust for anonymous users, then the script
 * itself — and a remote can mount and start tracking inside it.
 *
 * The cost is that when analytics.js never loads at all — an anonymous viewer
 * who declines the cookie category, or OneTrust failing — the snippet's queue
 * grows for the whole session, and some call sites are chatty (one
 * `Weave Panel Event` per panel). `QUEUE_LIMIT` bounds that: while the instance
 * is still the snippet's array, stop pushing once it is that long.
 */
const QUEUE_LIMIT = 500;

function call(
  method: keyof SegmentApi,
  invoke: (api: SegmentApi) => void
): void {
  const api = instance();
  /*
    An array means the snippet is still in place and analytics.js has not
    replaced it, so `length` is the number of queued calls waiting for replay.
  */
  if (Array.isArray(api) && api.length >= QUEUE_LIMIT) {
    return;
  }
  if (api == null || typeof api[method] !== 'function') {
    if (!warnedMissing) {
      warnedMissing = true;
      console.error(
        `Segment is absent, so analytics.${method} and everything after it is ` +
          'lost. `consent/analytics.ts` installs the snippet during `bootAnalytics`, ' +
          'so this means main.tsx did not boot analytics.'
      );
    }
    return;
  }
  try {
    invoke(api);
  } catch (error) {
    // The snippet's stubs cannot throw, and neither can a loaded analytics.js,
    // so this is a genuinely unexpected state rather than a lost event.
    console.error(`Segment ${method} threw`, error);
  }
}

/** Identity is the shell's alone; no remote gets a way to call these. */
export function identifyViewer(userId: string, traits: object): void {
  call('identify', api => api.identify(userId, traits));
}

/**
 * Runs `callback` once analytics.js has initialized its device-mode
 * destinations — the point at which a destination's own global (Chameleon's
 * `window.chmln`, say) exists.
 *
 * Goes through `instance()` like everything else here, rather than reading
 * `window.analytics`, per the module docblock. Safe before the library loads:
 * `ready` is one of the snippet's queued methods and is replayed in order.
 */
export function onSegmentReady(callback: () => void): void {
  const api = instance();
  if (typeof api?.ready !== 'function') {
    return;
  }
  api.ready(callback);
}

export function groupViewer(groupId: string, traits: object): void {
  call('group', api => api.group(groupId, traits));
}

// --- app_name --------------------------------------------------------------

/**
 * Applied last, so it wins over anything the caller passed. That is the point
 * of it: an attribution field a semi-hostile remote cannot overwrite by sending
 * its own `app_name`. Idempotent, which the proxy relies on.
 */
function stamp(properties: unknown, appName: string): AnalyticsProperties {
  const base =
    properties != null &&
    typeof properties === 'object' &&
    !Array.isArray(properties)
      ? (properties as AnalyticsProperties)
      : {};
  return {...base, app_name: appName};
}

/**
 * Adds the host session id to a `page` call's options, so that no remote has to
 * read the cookie itself. A caller's own `context` is merged, not replaced.
 */
/**
 * Segment's real signature is
 * `page(category?, name?, properties?, options?, callback?)`, so a legacy call
 * site can pass something other than options second.
 */
function isOptions(value: unknown): value is AnalyticsOptions | undefined {
  return value == null || (typeof value === 'object' && !Array.isArray(value));
}

function withHostSession(
  options: AnalyticsOptions | undefined
): AnalyticsOptions {
  const id = hostSessionId();
  if (id == null) {
    return options ?? {};
  }
  const existing = options?.context;
  const context =
    existing != null && typeof existing === 'object' && !Array.isArray(existing)
      ? existing
      : {};
  return {...options, context: {...context, hostSessionID: id}};
}

/**
 * One app's analytics: a remote's, or the shell's own. Closes over the name it
 * stamps and any scoped properties, and goes straight to the Segment instance
 * rather than through the `window.analytics` proxy — see the module docblock.
 *
 * A closure rather than a class because the result crosses the boundary into a
 * semi-hostile remote: there is no `this`, so `const {track} = props.analytics`
 * keeps working.
 */
function createAnalytics(
  appName: string,
  scope: AnalyticsProperties = {}
): ShellAnalytics {
  const properties = (extra: AnalyticsProperties | undefined) =>
    stamp({...scope, ...extra}, appName);

  return {
    track: (event, extra) =>
      call('track', api => api.track(event, properties(extra))),
    page: (extra, options) =>
      call('page', api =>
        api.page(properties(extra), withHostSession(options))
      ),
    scoped: extra => createAnalytics(appName, {...scope, ...extra}),
  };
}

/*
  Cached per remote so the handle's identity is stable. `RemoteMount` calls
  `instance.update(props)` whenever the props object's identity changes, and
  those props are a `useMemo` over their members — a handle rebuilt each render
  would re-run every remote's update path on every render.
*/
const handles = new Map<RemoteName, RemoteAnalytics>();

/**
 * `app_name` is the remote's name from the shell config, not
 * `productForRemote(remote).label` — that helper falls back to the first product
 * for an unrecognized remote, which would file every unknown remote's events
 * under ARIA.
 *
 * The install hatch is added here, on the handle a remote is mounted with,
 * rather than inside `createAnalytics` — so `scoped()` keeps returning the
 * narrow `ShellAnalytics` and child scopes do not carry it.
 */
export function analyticsForRemote(remote: RemoteName): RemoteAnalytics {
  let handle = handles.get(remote);
  if (!handle) {
    handle = {
      ...createAnalytics(remote),
      installSegmentGlobalProxy: () => installSegmentGlobalProxy(remote),
    };
    handles.set(remote, handle);
  }
  return handle;
}

/** The handle for the shell's own events. No install hatch: it is not a remote. */
export const analyticsForShell: ShellAnalytics =
  createAnalytics(SHELL_APP_NAME);

/** Segment's per-call options; only destination routing is used so far. */
export type SegmentTrackOptions = {
  integrations?: Record<string, boolean>;
};

/**
 * The shell's own `track` with Segment's options argument, which the remote
 * contract leaves out. For the few events a destination has to be enabled for
 * explicitly (the app sends its signup events to Salesforce this way).
 */
export function trackForShell(
  event: string,
  properties?: AnalyticsProperties,
  options?: SegmentTrackOptions
): void {
  call('track', api =>
    api.track(event, stamp(properties, SHELL_APP_NAME), options)
  );
}

/**
 * The handle for the ARIA header slice, stamping `aria` rather than the `wandb`
 * of the container it ships in.
 *
 * `ShellAnalytics` rather than `RemoteAnalytics`, deliberately: ARIA reads
 * `window.analytics` from nowhere in its own tree, and the global proxy is
 * page-wide while installing is per app — handing ARIA the hatch would let it
 * clobber the wandb remote's install and restamp that remote's ~250 legacy call
 * sites as `aria`.
 */
export const analyticsForAria: ShellAnalytics =
  createAnalytics(ARIA_PRODUCT_ID);

// --- The window.analytics compatibility accessor ---------------------------

/**
 * Opt-in, per remote. wandb reaches for the Segment global from ~250 places,
 * many of them plain utility functions, and none of them are moving this
 * quarter; rather than leave those events unattributed, that remote asks for a
 * proxy over the global. A remote that does not ask gets an untouched
 * `window.analytics`, and the shell installs nothing at boot.
 *
 * An accessor rather than a plain wrapper, because `window.analytics` is
 * reassigned repeatedly and a wrapper would be clobbered.
 */

function createHandler(appName: string): ProxyHandler<object> {
  return {
    get(target, property, receiver) {
      const value = Reflect.get(target, property, receiver);
      if (typeof value !== 'function') {
        return value;
      }
      const fn = value as (...args: unknown[]) => unknown;

      /*
        `fn` is the function read *now*: the wrappers below call it directly
        rather than resolving the method again, so a caller that reflects over
        the global and reassigns each method — as the standalone app's
        `consentAwareAnalyticsInjector.js` does — cannot make a wrapper find
        itself. Verified: a version that re-resolves blows the stack.

        Only `track` and `page` are stamped. Everything else passes through:
        this is a compatibility shim, not an access-control boundary.
      */
      if (property === 'track') {
        // track(event, properties, ...) — `properties` is the second argument.
        return (...args: unknown[]) =>
          fn.apply(target, [
            args[0],
            stamp(args[1], appName),
            ...args.slice(2),
          ]);
      }
      if (property === 'page') {
        /*
          page(properties, options, ...). The id goes on here too, so a legacy
          call and a contract call produce the same payload.
        */
        return (...args: unknown[]) => {
          const stamped = stamp(args[0], appName);
          /*
            Only an options-shaped second argument is rewritten: a
            `page(properties, callback)` whose callback was spread into an
            options object would never fire.
          */
          if (hostSessionId() == null || !isOptions(args[1])) {
            return fn.apply(target, [stamped, ...args.slice(1)]);
          }
          return fn.apply(target, [
            stamped,
            withHostSession(args[1]),
            ...args.slice(2),
          ]);
        };
      }

      /*
        Bound, because call sites read a method off the global and invoke it
        detached — `const t = window.analytics?.track; t(name, data)` is exactly
        what the app's `services/analytics/Analytics.ts` used to do.
      */
      return fn.bind(target);
    },
    // Writes land on the instance, not the shim.
    set(target, property, value) {
      return Reflect.set(target, property, value);
    },
  };
}

/**
 * Rebuilt only when the instance changes: a `Proxy` is bound to the target it
 * was constructed with, and the instance gets replaced.
 */
let proxy: SegmentApi | undefined;
let proxyTarget: SegmentApi | undefined;

function currentProxy(handler: ProxyHandler<object>): SegmentApi | undefined {
  const target = instance();
  if (target == null) {
    return undefined;
  }
  if (proxy == null || proxyTarget !== target) {
    proxyTarget = target;
    proxy = new Proxy(target as object, handler) as SegmentApi;
  }
  return proxy;
}

/**
 * Replaces `window.analytics` with an accessor whose proxy stamps `appName`.
 *
 * Reached only through `RemoteAnalytics.installSegmentGlobalProxy`, so it runs
 * when a remote that needs it mounts.
 */
export function installSegmentGlobalProxy(appName: string): void {
  /*
    `instance()`, not `readGlobal()`: if a proxy is already installed the global
    *is* that proxy, and adopting it would nest one proxy inside another — the
    inner stamp would then overwrite the outer one, so the new name would lose.
  */
  segment = instance();
  proxy = undefined;
  proxyTarget = undefined;
  const handler = createHandler(appName);

  try {
    Object.defineProperty(window, 'analytics', {
      configurable: true,
      get: () => currentProxy(handler),
      set(next: SegmentApi | undefined) {
        segment = next;
      },
    });
    accessorInstalled = true;
  } catch (error) {
    // A non-configurable global means someone else claimed it first. Losing
    // `app_name` is better than failing to boot the shell.
    console.error('Could not install the Segment global proxy', error);
  }
}

/**
 * Puts the real instance back on `window.analytics` as a plain property.
 *
 * The shell calls this when the remote unmounts rather than handing the remote
 * a disposer: the shell owns the mount lifecycle, and a remote that dropped its
 * disposer would leave the proxy stamping its name over an empty page.
 *
 * There is one proxy for the page even though installing is per remote, which
 * is fine while wandb is the only remote that opts in. If a second one ever
 * does, both would be stamped with whichever installed last, and the first to
 * unmount would remove the proxy from under the other — so give this a
 * reference count before adding a second caller.
 */
export function uninstallSegmentGlobalProxy(): void {
  if (!accessorInstalled) {
    return;
  }
  const current = segment;
  accessorInstalled = false;
  proxy = undefined;
  proxyTarget = undefined;

  try {
    Object.defineProperty(window, 'analytics', {
      configurable: true,
      enumerable: true,
      writable: true,
      value: current,
    });
  } catch (error) {
    console.error('Could not remove the Segment global proxy', error);
  }
}

/** Test seam: module state is per-page, so suites must clear it. */
export function resetSegmentForTests(): void {
  resetHostSessionForTests();
  segment = undefined;
  accessorInstalled = false;
  warnedMissing = false;
  proxy = undefined;
  proxyTarget = undefined;
  handles.clear();
  try {
    delete (window as {analytics?: unknown}).analytics;
  } catch {
    // A suite that made the property non-configurable on purpose.
  }
}
